import { Fetcher } from "query/Fetch";
import { AndClause, ClauseDispatcher, ColumnClause, ColumnPath, LiteralClause, OperatorClause, OrClause, OrdOperator, RootClause, SearchClause } from "query/Filter";
import Query from "../query/Query";
import { QueryColumn } from "../query/QueryColumn";
import { Row } from "./Row";
import Table from "./Table";
import TableColumn from "./TableColumn";
import { ColumnType } from "query/ColumnType";
import { OrderedBy, OrderedByEntry } from "query/MultipleQueryColumn";

interface CollectionTableColumn {
    name: string;
    type?: ColumnType;
}

interface ComplexCollectionTableColumn {
    name: string;
    contents: CollectionColumnDefs[];
}

type CollectionColumnDefs = CollectionTableColumn | ComplexCollectionTableColumn

/* I am a table based on an array of arrays. The outside array is the rows. The inside arrays are
the cells in each row.

The inside arrays can be a hierarchy of arrays within arrays until we reach the actual column values.

*/
type Path = number[]; // sequence of indexes into a column tree to find a particular column.

export class CollectionTable extends Table {
    contents: Array<Array<any>>;

    constructor(columns: Array<CollectionColumnDefs>, contents: Array<any>) {
        super();
        this.addColumns(columns, this._columns);
        this.contents = contents;
    }

    private addColumns = (defs: CollectionColumnDefs[], parent: TableColumn) => {
        defs.forEach(each => {
            if ('contents' in each) {
                let t = new TableColumn(each.name, ColumnType.String);
                this.addColumns(each.contents, t);
                parent.children.push(t);
            } else {
                let t = new TableColumn(each.name, each.type ?? ColumnType.String);
                parent.children.push(t);
            }
        });
    }

    count = () => {
        return this.contents.length;
    }

    fetch = (unused: Fetcher, q: Query, from: number, to: number) => {
        let selectedColumns: QueryColumn[] = [];
        this.flatten(selectedColumns, q);
        let paths: Path[];
        let result: Row[] = [];

        // TODO: cache results if the query hasn't changed.
        // We can keep filtered and ordered as well if only pagination changes.
        let filtered: number[] = this.filter(q);
        let ordered: number[] = this.order(q, filtered);
        let paginated: number[] = ordered.slice(from, to);

        // `paths` is the selected columns. The source is a hierarchy of arrays.
        paths = makePaths(selectedColumns, q.table);

        for (let eachIndex of paginated) {
            let row = this.contents[eachIndex];
            let cells: any[] = [];
            addCellsOfRow(row, paths, cells);
            let newRow = new Row(q);
            newRow.cells = cells;
            result.push(newRow);
        }

        return new Promise<Row[]>((resolve, reject) => resolve(result));
    }

    private filter = (q: Query) => {
        let result: number[] = [];
        let filterer : FilterAcceptor = new FilterAcceptor(this._columns);

        for (let i = 0; i < this.contents.length; i++) {
            if (this.filterRow(q, filterer, this.contents[i])) {
                result.push(i);
            }
        }
        return result;
    }

    private filterRow: (q: Query, filterer: FilterAcceptor, row: any[]) => boolean
        = (q, filterer, row) => {
            filterer.row = row;
            filterer.result = false;
            q.filter.visit(filterer);
            return filterer.result;
        }

    private order : (q: Query, indexes : number[]) => number[]  
        = (q, indexes) => {
            if (q.getOrderBy().length === 0) {
                return indexes;
            }
            let result : number[] = [...indexes];
            // TODO: order by more than one column.
            let path : ColumnPath = this.orderByToColumnPath(q.getOrderBy());

            switch(q.getOrderBy()[0].orderedBy) {
                case OrderedBy.ASC: 
                    result.sort( (a, b) => magnitude(getValue(this.contents[a], this.columns, path) <= getValue(this.contents[b], this.columns, path)));
                    break;
                case OrderedBy.DESC:
                    result.sort( (a, b) => magnitude(getValue(this.contents[a], this.columns, path) >= getValue(this.contents[b], this.columns, path)));
                    break;
            }
            return result;
        }

    private orderByToColumnPath = (orderBy: OrderedByEntry[]) => {
        let result : ColumnPath = [];

        for (let each of orderBy) {
            let t : TableColumn|undefined = each.column._basedOn;
            if (t) {
                result.push(t);
            }
        }
        return result;
    }

    private flatten = (result: QueryColumn[], q: QueryColumn) => {
        if (q.columns.length > 0) {
            for (let qeach of q.columns) {
                if (qeach.isExpanded) {
                    this.flatten(result, qeach);
                } else {
                    result.push(qeach);
                }
            }
        }
        else {
            result.push(q);
        }
    }

    async fetchCount(_: Fetcher, q: Query): Promise<number> {
        return this.contents.length;
    }

    refetchColumns(): void {}
}

function magnitude(b: boolean) {
    switch (b) {
        case true:
            return 1;
        case false:
            return -1;
    }
}

function makePaths(columns: QueryColumn[], table: Table) {
    let result: Path[] = [];
    for (let c of columns) {
        let thisPath = pathOf(c._basedOn, table.columns);
        if (null !== thisPath) {
            result.push(thisPath);
        } else {
            result.push([]);
        }

    }
    return result;
}

/** Return the path of the c, from the TableColumn t as the root.
 * TODO: not used?
 */
function pathOf(c: TableColumn|undefined, t: TableColumn) : (Path | null) {
        if (!c) return null;
        let index = 0;
        for (let tEach of t.children) {
            if (tEach === c) {
                return [index];
            }
            if (tEach.children.length > 0) {
                let r: Path | null = pathOf(c, tEach);
                if (null !== r) {
                    return [index].concat(r);
                }
            }
            index++;
        }
        return null;
    }

/* Add the cells from `source` to `cells`, following the paths specified. */
function addCellsOfRow(source: any[], paths: Path[], cells: any[]) {
    for (let path of paths) {
        addCell(source, path, cells);
    }
}

function addCell (source: any[], path: Path, cells: any[]) {
    if (0 === path.length) {
        cells.push("???");
    } else if (1 === path.length) {
        cells.push(source[path[0]]);
    } else {
        addCell(source[path[0]], path.slice(1), cells);
    }
}

function getValue(source: any[], t:TableColumn, path: ColumnPath) : any {
    if (0 === path.length) {
        return "???";
    } else if (1 === path.length) {
        let index = t.children.findIndex(each => {return each === path[0]})
        return source[index];
    } else {
        let index = t.children.findIndex(each => {return each === path[0]})
        return getValue(source[index], t, path.slice(1));
    }
}

/* I'm an 'interpreter' for a filter. Given a row, I run the filter on that row. */
class FilterAcceptor implements ClauseDispatcher {
    root : TableColumn;
    result : boolean = false;
    row : any;

    constructor(fromRoot: TableColumn) {
        this.root = fromRoot;
    }

    acceptRoot(v: RootClause): void {
        v.clause.visit(this);
    }

    acceptOperator(v: OperatorClause): void {
        if (!(
            v.left instanceof ColumnClause
            && v.right instanceof LiteralClause)) {
            throw new Error("Not implemented");
        }

        let left: ColumnClause = v.left;
        if (left.column === undefined) {
            this.result = true;
            return;
        }

        let right: LiteralClause = v.right;
         if (String(right.value).length === 0) {
            this.result = true; // Pretend that this clause hasn't been added yet.
            return;
        }

        let value: any = getValue(this.row, this.root, left.column);

        switch (v.operator) {
            case OrdOperator.equals:
                this.result = String(value) === String(right.value);
                break;
            case OrdOperator.lessThan:
                // TODO: right.value is a string.
                this.result = value < right.value;
                break;
            case OrdOperator.greaterThan:
                this.result = value > right.value;
                break;
            // TODO: more operators, and operators specific to types e.g. strings, geo.
        }
    }

    acceptSearch(v: SearchClause): void {
        for (let each of this.row) {
            if (String(each).toLowerCase().includes(v.value.toLowerCase())) {
                this.result = true;
                break;
            }
        }
    }

    acceptAnd(v: AndClause): void {
        v.left.visit(this);
        if (this.result) {
            v.right.visit(this);
        }
    }

    acceptOr(v: OrClause): void {
        v.left.visit(this);
        if (this.result) {
            return
        }
        v.right.visit(this);
    }

    acceptColumnChoice(v: ColumnClause): void { }

    acceptLiteral(v: LiteralClause): void { }
}